import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { AuthService } from '../../../../../../../../../shared/services/auth.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';

@Component({
  standalone: false,
  selector: 'app-bid-items-fields',
  templateUrl: './bid-items-fields.component.html',
  styleUrls: ['./bid-items-fields.component.scss'],
})
export class BidItemsFieldsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  @Input() projectId: string | null = null;

  canEdit = false;
  fieldsList: any[] = [];
  accordionGroups: { id: string; label: string; field: any }[] = [];
  selectedAccordionId: string | null = null;
  mainForm = new FormGroup({});
  private readonly onDestroy = new Subject<void>();

  constructor(
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.updateCanEdit();
    this.buildFromData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (!changes['data'].firstChange) {
        this.buildFromData();
      }
      this.updateCanEdit();
    }
    if (changes['projectId']) {
      this.updateCanEdit();
    }
  }

  private updateCanEdit(): void {
    const account = this.authService.getCurrentUser();
    const projectId = this.projectId ?? this.data?.projectId ?? this.data?.project_id ?? null;
    const projectRole =
      account?.roles?.length && projectId != null
        ? account.roles.find((r: any) => String(r?.project_id) === String(projectId))?.role ?? account?.role
        : account?.role;
    this.canEdit = projectRole === 'org-admin' || projectRole === 'project-manager';
  }

  private buildFromData(): void {
    this.fieldsList = Array.isArray(this.data?.fields)
      ? this.data.fields
      : (this.data?.fields?.list ?? []);
    const fromData = this.buildAccordionGroups(this.fieldsList);
    this.accordionGroups = [...fromData].sort(
      (a, b) => (a.field?.display_order ?? 0) - (b.field?.display_order ?? 0)
    );
    this.selectedAccordionId = null;
    this.buildForm();
  }

  buildAccordionGroups(list: any[]): { id: string; label: string; field: any }[] {
    if (!list?.length) return [];
    const first = list[0];
    if (first?.fields && Array.isArray(first.fields)) {
      return list.reduce(
        (acc: { id: string; label: string; field: any }[], section: any) => [
          ...acc,
          ...(section?.fields ?? []).map((field: any, j: number) => ({
            id: field?.id ?? field?.name ?? `${section?.name ?? section?.id ?? 'section'}_${j}`,
            label: field?.label ?? field?.name ?? `Field ${j + 1}`,
            field,
          })),
        ],
        []
      );
    }
    return list.map((field, i) => ({
      id: field?.id ?? field?.name ?? `field_${i}`,
      label: field?.label ?? field?.name ?? `Field ${i + 1}`,
      field,
    }));
  }

  buildForm(): void {
    const next = new FormGroup({});
    this.accordionGroups.forEach(group => {
      const newGroup = this.createFieldFormGroup(group.field);
      const existing = this.mainForm.get(group.id);
      if (existing instanceof FormGroup && existing.getRawValue) {
        newGroup.patchValue(existing.getRawValue());
      }
      next.addControl(group.id, newGroup);
    });
    this.mainForm = next;
  }

  createFieldFormGroup(field: any): FormGroup {
    const vr = field?.validation_rules ?? {};
    const rv = field?.range_values ?? {};
    return new FormGroup({
      label: new FormControl(field?.label ?? '', [Validators.required]),
      name: new FormControl(field?.name ?? '', [Validators.required]),
      type: new FormControl(field?.type ?? '', [Validators.required]),
      uom: new FormControl(field?.uom ?? ''),
      isOptional: new FormControl(field?.is_optional ?? false),
      required: new FormControl(vr?.required ?? false),
      required_min: new FormControl(vr?.min ?? vr?.required_min ?? null),
      required_max: new FormControl(vr?.max ?? vr?.required_max ?? null),
      range_min: new FormControl(rv?.min ?? null),
      range_max: new FormControl(rv?.max ?? null),
    });
  }

  getFieldFormGroup(group: { id: string; label: string; field: any }): FormGroup | null {
    const control = this.mainForm.get(group.id);
    return control instanceof FormGroup ? control : null;
  }

  getAccordionLabel(group: { id: string; label: string; field: any }): string {
    const formGroup = this.getFieldFormGroup(group);
    const label = formGroup?.get('label')?.value;
    return (label != null && String(label).trim() !== '') ? String(label).trim() : (group.label || 'New field');
  }

  selectAccordion(group: { id: string; label: string; field: any }): void {
    this.selectedAccordionId =
      this.selectedAccordionId === group.id ? null : group.id;
  }

  addAccordion(): void {
    const tempId = `temp_${Date.now()}`;
    this.mainForm.addControl(tempId, this.createFieldFormGroup({}));
    this.mainForm.markAsDirty();
    this.accordionGroups = [
      { id: tempId, label: 'New field', field: {} },
      ...this.accordionGroups,
    ];
    this.selectedAccordionId = tempId;
  }

  confirmAndDeleteGroup(group: { id: string; label: string; field: any }): void {
    const label = this.getAccordionLabel(group) || 'this field';
    const message = `Delete <b>${label}</b>? This cannot be undone.`;
    const dialogData = new ConfirmDialogModel('Delete?', message);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(result => {
      if (result) {
        this.onFieldDelete(group.id);
      }
    });
  }

  onFieldDelete(groupId: string): void {
    const group = this.accordionGroups.find(g => g.id === groupId);
    const fieldId = group?.field?.id;

    const removeFromUI = (): void => {
      this.accordionGroups = this.accordionGroups.filter(g => g.id !== groupId);
      this.mainForm.removeControl(groupId);
      if (this.selectedAccordionId === groupId) {
        this.selectedAccordionId = null;
      }
    };

    if (fieldId && !groupId.startsWith('temp_')) {
      this.projectsService.deleteField(fieldId).subscribe({
        next: () => {
          this.commonService.notification('Field deleted successfully', 'success');
          removeFromUI();
        },
        error: () => {
          this.commonService.notification('Failed to delete field', 'error');
        },
      });
    } else {
      removeFromUI();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
